import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, Check, ChevronRight, Plus, Sparkles, Users, Video, Target, Star,
} from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { Pricing } from '../components/Pricing';
import { Testimonials } from '../components/Testimonials';
import Seo from '../components/Seo';
import { useLanguage } from '../context/LanguageContext';
import { getLanding, HOME_PATHS, SITE_URL, hreflangFor, LANDINGS } from '../config/landings';

const UI = {
  es: { home: 'Inicio', lessons: 'Clases online', benefits: 'Lo que vas a conseguir', how: 'Cómo funcionan las clases', why: 'Por qué aprender conmigo', faq: 'Preguntas frecuentes', prices: 'Tarifas', related: 'También te puede interesar', finalTitle: '¿Empezamos?', finalText: 'Reserva una clase de prueba gratuita de 30 minutos. Nos conocemos, valoramos tu nivel y definimos un plan. Sin compromiso.' },
  de: { home: 'Start', lessons: 'Online-Unterricht', benefits: 'Das erreichst du', how: 'So läuft der Unterricht ab', why: 'Warum mit mir lernen', faq: 'Häufige Fragen', prices: 'Preise', related: 'Das könnte dich auch interessieren', finalTitle: 'Sollen wir starten?', finalText: 'Buche eine kostenlose Probestunde von 30 Minuten. Wir lernen uns kennen, schauen dein Niveau an und legen einen Plan fest. Unverbindlich.' },
  en: { home: 'Home', lessons: 'Online lessons', benefits: 'What you will achieve', how: 'How the lessons work', why: 'Why learn with me', faq: 'Frequently asked questions', prices: 'Prices', related: 'You may also like', finalTitle: 'Shall we start?', finalText: 'Book a free 30-minute trial lesson. We get to know each other, assess your level and set a plan. No commitment.' },
};

export default function ServiceLandingPage({ path }) {
  const location = useLocation();
  const landing = getLanding(path || location.pathname.replace(/\/+$/, '') || '/');
  const { language } = useLanguage();
  const [openFaq, setOpenFaq] = useState(0);

  // El HTML prerenderizado ya trae su JSON-LD; al hidratar lo sustituye el de Helmet.
  useEffect(() => {
    document
      .querySelectorAll('script[type="application/ld+json"][data-prerendered]')
      .forEach((el) => el.remove());
  }, [path]);

  if (!landing) return <Navigate to="/" replace />;

  const ui = UI[landing.lang] || UI.es;
  const home = HOME_PATHS[landing.lang];
  const bookingUrl = `${home === '/' ? '' : home}/#booking`;
  const related = (landing.related || [])
    .map((slug) =>
      slug === 'preparacion-dele' && landing.lang === 'es'
        ? { path: '/preparacion-dele', h1: 'Preparación DELE online con examinadora acreditada' }
        : LANDINGS.find((l) => l.lang === landing.lang && l.slug === slug)
    )
    .filter(Boolean);

  const url = `${SITE_URL}${landing.path}`;
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: landing.h1,
      serviceType: landing.breadcrumb,
      description: landing.seoDescription,
      url,
      areaServed: 'Worldwide',
      availableChannel: { '@type': 'ServiceChannel', serviceUrl: url },
      provider: {
        '@type': 'Person',
        name: 'Cecilia Mateo García',
        jobTitle: 'Profesora de español y examinadora DELE',
        url: SITE_URL,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: ui.home, item: `${SITE_URL}${home}` },
        { '@type': 'ListItem', position: 2, name: landing.breadcrumb, item: url },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: landing.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Seo
        fullTitle={landing.seoTitle}
        description={landing.seoDescription}
        path={landing.path}
        hreflang={hreflangFor(landing.path)}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-14 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#E8F5FA] to-white">
        <div className="max-w-4xl mx-auto">
          <nav aria-label="breadcrumb" className="flex items-center gap-1.5 text-xs text-gray-500 mb-6">
            <Link to={home} className="hover:text-[#7DBDD9]" data-testid="landing-breadcrumb-home">{ui.home}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#4A4A4A] font-semibold">{landing.breadcrumb}</span>
          </nav>

          <span className="inline-flex items-center gap-2 text-[#E38987] font-semibold text-xs uppercase tracking-[0.18em]">
            <Sparkles className="w-4 h-4" />
            {landing.kicker}
          </span>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#4A4A4A] leading-[1.12] mt-4 mb-5"
            data-testid="landing-h1"
          >
            {landing.h1}
          </motion.h1>

          <p className="text-base md:text-lg text-[#4A4A4A]/80 leading-relaxed mb-8" data-testid="landing-lead">
            {landing.lead}
          </p>

          <a
            href={bookingUrl}
            data-testid="landing-cta-hero"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#7DBDD9] hover:bg-[#5DA6C9] text-white rounded-full font-semibold shadow-lg hover:shadow-xl transition-all"
          >
            {landing.cta}
            <ArrowRight className="w-4 h-4" />
          </a>

          <div className="mt-10 space-y-4">
            {landing.intro.map((p, i) => (
              <p key={i} className="text-base text-gray-600 leading-relaxed">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-[#4A4A4A] mb-2">{ui.benefits}</h2>
          <div className="w-16 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mb-10" />
          <div className="grid sm:grid-cols-2 gap-6">
            {landing.benefits.map((b, i) => (
              <motion.div
                key={b.t}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="p-6 rounded-3xl bg-gradient-to-br from-[#F7FBFD] to-white border border-[#A8D8EA]/40 shadow-sm"
                data-testid={`landing-benefit-${i}`}
              >
                <h3 className="text-base font-bold text-[#4A4A4A] mb-2">{b.t}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{b.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Para quién es */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-[#FFF8F7]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-[#4A4A4A] mb-2 flex items-center gap-3">
            <Users className="w-6 h-6 text-[#E38987]" />
            {landing.audienceTitle}
          </h2>
          <div className="w-16 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mb-8" />
          <ul className="grid sm:grid-cols-2 gap-4">
            {landing.audience.map((a, i) => (
              <li key={a} className="flex items-start gap-3 bg-white rounded-2xl p-4 shadow-sm" data-testid={`landing-audience-${i}`}>
                <Check className="w-4 h-4 text-[#7DBDD9] flex-shrink-0 mt-1" />
                <span className="text-sm text-gray-600 leading-relaxed">{a}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Cómo funcionan + qué trabajaremos */}
      <section className="py-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#4A4A4A] mb-2 flex items-center gap-3">
              <Video className="w-6 h-6 text-[#7DBDD9]" />
              {ui.how}
            </h2>
            <div className="w-16 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mb-8" />
            <div className="space-y-4">
              {landing.howItWorks.map((h, i) => (
                <div key={h.t} className="flex items-start gap-3" data-testid={`landing-how-${i}`}>
                  <span className="w-7 h-7 rounded-xl bg-[#E8F5FA] text-[#7DBDD9] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span>
                    <strong className="block text-sm font-bold text-[#4A4A4A]">{h.t}</strong>
                    <span className="text-sm text-gray-600 leading-relaxed">{h.d}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#4A4A4A] mb-2 flex items-center gap-3">
              <Target className="w-6 h-6 text-[#E38987]" />
              {landing.whatWeWorkTitle}
            </h2>
            <div className="w-16 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mb-8" />
            <ul className="space-y-3">
              {landing.whatWeWork.map((w, i) => (
                <li key={w} className="flex items-start gap-3" data-testid={`landing-work-${i}`}>
                  <Check className="w-4 h-4 text-[#7DBDD9] flex-shrink-0 mt-1" />
                  <span className="text-sm text-gray-600 leading-relaxed">{w}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Por qué conmigo */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-[#E8F5FA]/60">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-[#4A4A4A] mb-2">{landing.whyMeTitle || ui.why}</h2>
          <div className="w-16 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mb-8" />
          <ul className="space-y-3">
            {landing.whyMe.map((w, i) => (
              <li key={w} className="flex items-start gap-3" data-testid={`landing-why-${i}`}>
                <Star className="w-4 h-4 text-[#E38987] flex-shrink-0 mt-1" />
                <span className="text-sm md:text-base text-gray-700 leading-relaxed">{w}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Testimonios y tarifas reales reutilizando los componentes de la web */}
      <Testimonials />
      <Pricing />

      {/* FAQ */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-[#E8F5FA]">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-[#4A4A4A] mb-2">{ui.faq}</h2>
          <div className="w-16 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mb-8" />
          <div className="space-y-3">
            {landing.faq.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={f.q} className={`rounded-2xl bg-white border transition-colors ${isOpen ? 'border-[#7DBDD9] shadow-lg' : 'border-gray-100 shadow-sm'}`}>
                  <button
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    data-testid={`landing-faq-q-${i}`}
                    className="w-full flex items-center justify-between gap-4 text-left px-6 py-5"
                  >
                    <h3 className="text-base font-semibold text-[#4A4A4A]">{f.q}</h3>
                    <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all ${isOpen ? 'bg-[#7DBDD9] rotate-45' : 'bg-[#E8F5FA]'}`}>
                      <Plus className={`w-4 h-4 ${isOpen ? 'text-white' : 'text-[#7DBDD9]'}`} />
                    </span>
                  </button>
                  {isOpen && (
                    <p className="px-6 pb-6 text-sm text-gray-600 leading-relaxed" data-testid={`landing-faq-a-${i}`}>
                      {f.a}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Enlaces internos + CTA final */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {related.length > 0 && (
            <>
              <h2 className="text-xl font-bold text-[#4A4A4A] mb-6">{ui.related}</h2>
              <div className="grid sm:grid-cols-3 gap-4 mb-14">
                {related.map((r, i) => (
                  <Link
                    key={r.path}
                    to={r.path}
                    data-testid={`landing-related-${i}`}
                    className="group p-5 rounded-2xl bg-white border border-[#A8D8EA]/40 shadow-sm hover:shadow-lg transition-all"
                  >
                    <span className="block text-sm font-bold text-[#4A4A4A] mb-2 leading-snug">{r.h1}</span>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#5DA6C9]">
                      {UI[landing.lang].related ? (landing.lang === 'es' ? 'Ver más' : landing.lang === 'de' ? 'Mehr erfahren' : 'Learn more') : ''}
                      <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                ))}
              </div>
            </>
          )}

          <div className="rounded-3xl p-8 md:p-12 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-center shadow-xl">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4">{ui.finalTitle}</h2>
            <p className="text-white/90 text-base mb-8 max-w-2xl mx-auto leading-relaxed">{ui.finalText}</p>
            <a
              href={bookingUrl}
              data-testid="landing-cta-final"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#4A4A4A] rounded-full font-bold shadow-lg hover:shadow-2xl transition-all"
            >
              {landing.cta}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
